import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import Keycloak from 'keycloak-js';
import { setApiToken, setTokenRefreshCallback } from '../services/api';
import { logger } from '../services/logger';
import LoadingScreen from '../components/LoadingScreen';
import LoggedOutScreen from '../components/LoggedOutScreen';

interface CustomerRole {
  customerId: string;
  roles: string[];
}

interface KeycloakContextType {
  keycloak: Keycloak | null;
  initialized: boolean;
  authenticated: boolean;
  user: {
    id?: string;
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
  } | null;
  roles: string[];
  condos: string[];
  groups: string[];
  customerRoles: CustomerRole[];  // NEW: Customer-scoped roles
  isSuperAdmin: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (...roles: string[]) => boolean;
  hasAccessToCondo: (condoId: string) => boolean;
  getRolesForCustomer: (customerId: string) => string[];  // NEW: Get roles for specific customer
  login: () => void;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  retryAuth: () => void;
  token: string | undefined;
  isOnline: boolean;
}

/**
 * Result of one refresh attempt.
 *
 * `ok` distinguishes "the refresh call completed" from "it failed", and `refreshed`
 * distinguishes "a new token was issued" from "the existing one was still valid". The
 * interval needs both: a still-valid token is its cue to probe connectivity instead.
 */
type RefreshOutcome = { ok: true; refreshed: boolean } | { ok: false };

type RefreshTokensFn = (minValidity: number, reason: string) => Promise<RefreshOutcome>;

const KeycloakContext = createContext<KeycloakContextType>({
  keycloak: null,
  initialized: false,
  authenticated: false,
  user: null,
  roles: [],
  condos: [],
  groups: [],
  customerRoles: [],
  isSuperAdmin: false,
  hasRole: () => false,
  hasAnyRole: () => false,
  hasAccessToCondo: () => false,
  getRolesForCustomer: () => [],
  login: () => {},
  logout: () => {},
  refreshToken: async () => false,
  retryAuth: () => {},
  token: undefined,
  isOnline: true,
});

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error('useKeycloak must be used within KeycloakProvider');
  }
  return context;
};

interface KeycloakProviderProps {
  children: React.ReactNode;
}

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ children }) => {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<KeycloakContextType['user']>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [condos, setCondos] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [customerRoles, setCustomerRoles] = useState<CustomerRole[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const initializingRef = useRef(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  // Holds the keycloakInstance so the LoggedOutScreen's login button can use it.
  const keycloakInstanceRef = useRef<Keycloak | null>(null);

  // Single funnel for every token refresh in this provider.
  //
  // There are six refresh triggers (the 10 s interval, Keycloak's onTokenExpired hook,
  // visibilitychange, the window 'online' event, the refreshToken exposed on the
  // context, and retryAuth). They used to call keycloakInstance.updateToken directly,
  // uncoordinated, each with its own success and failure handling. Two consequences:
  //
  //   - The failure path ran once per caller, so a single expired session could clear
  //     the stored tokens repeatedly and emit several session-expired events.
  //   - With refresh-token rotation enabled, two concurrent *forced* refreshes race for
  //     one refresh token. The loser gets invalid_grant, which the failure path reads as
  //     "session expired" and responds to by clearing tokens — logging out a user whose
  //     session was in fact perfectly valid. Returning to an idle screen fires both the
  //     visibilitychange refresh and an interval tick, which is exactly that race.
  //
  // Sharing one in-flight promise makes concurrent triggers collapse into a single
  // round-trip, and makes the failure path run once per attempt rather than once per
  // caller.
  const inflightRefreshRef = useRef<Promise<RefreshOutcome> | null>(null);
  const refreshTokensRef = useRef<RefreshTokensFn | null>(null);
  const [isOnline, _setIsOnline] = useState(true);
  // Ref tracks online state synchronously — closures (refreshToken, callbacks) read
  // this instead of the stale useState value.
  const isOnlineRef = useRef(true);
  const setOnlineState = (online: boolean) => {
    isOnlineRef.current = online;
    _setIsOnline(online);
    // Write to localStorage immediately so a hard reload (Ctrl+Shift+R) shows the
    // offline banner instantly without waiting for checkReachable() to resolve.
    if (online) {
      localStorage.removeItem('panther:offline');
    } else {
      localStorage.setItem('panther:offline', '1');
    }
    // Notify NetworkStatusBanner which lives outside KeycloakProvider in the tree.
    window.dispatchEvent(new CustomEvent('panther:connectivity', { detail: { online } }));
  };

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initializingRef.current) {
      return;
    }
    initializingRef.current = true;

    // Check if current path is a public route (no authentication required)
    const currentPath = window.location.pathname;
    const isPublicRoute = currentPath.startsWith('/pre-register') || currentPath.startsWith('/invite/') || currentPath.startsWith('/privacidade') || currentPath.startsWith('/r/') || currentPath.startsWith('/suporte') || currentPath.startsWith('/landingpage') || currentPath.startsWith('/politica-csae') || currentPath.startsWith('/solicitar-exclusao') || currentPath.startsWith('/instalar-android');

    // For public routes, skip Keycloak initialization entirely
    if (isPublicRoute) {
      console.log(' [PUBLIC ROUTE] Skipping Keycloak initialization for:', currentPath);
      // Clear any stale offline flag — public routes are always accessible online
      localStorage.removeItem('panther:offline');
      window.dispatchEvent(new CustomEvent('panther:connectivity', { detail: { online: true } }));
      setInitialized(true);
      setAuthenticated(false);
      setAnimationComplete(true); // Skip loading animation for public routes
      return;
    }

    // Get Keycloak config from environment variables
    // Empty REACT_APP_KEYCLOAK_URL = on-premise mode: Keycloak is proxied by nginx on the same origin
    const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || window.location.origin;
    const realm = process.env.REACT_APP_KEYCLOAK_REALM || 'panthercondos';
    const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'pantheraccess-web';

    // Use the base URL from environment, or current origin for Electron
    // In Electron dev mode, window.location.origin will be http://localhost:3000
    const redirectUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

    const startTime = Date.now();
    logger.info('keycloak.init', 'Initializing Keycloak', { keycloakUrl, realm, clientId, redirectUrl, currentOrigin: window.location.origin, currentPath });
    console.log(' [AUTH START] Keycloak initialization started at:', startTime);

    // Variables declared in outer scope so the synchronous cleanup function can access them
    // even though the init logic runs inside an async IIFE below.
    let refreshInterval: NodeJS.Timeout | null = null;
    let handleOnline: (() => void) | null = null;
    let handleOffline: (() => void) | null = null;
    let removeAuthOfflineListener: (() => void) | undefined;
    // Hoisted so cleanup can remove the exact listener instance. Declaring it only
    // inside the async IIFE below left cleanup unable to reference it.
    let visibilityListener: (() => void) | null = null;

    // Set up the offline handler immediately — before the async fetch check — so
    // any network drop that occurs during the startup sequence is captured even
    // if it happens between the fetch passing and Keycloak init completing.
    // (handleOnline is set up later, inside the IIFE, because it needs keycloakInstance.)
    handleOffline = () => setOnlineState(false);
    window.addEventListener('offline', handleOffline);

    // navigator.onLine is unreliable in Electron dev (returns true because the local
    // dev server at localhost is accessible). Instead, attempt a real fetch to the
    // Keycloak server — it fails immediately when internet is disconnected.
    // Note: do NOT use mode:'no-cors' here — opaque responses mask cached hits
    // and would let the check pass even when offline.
    (async () => {
      // If the main process flagged this load as an offline recovery (Keycloak
      // navigation failed in did-fail-load), skip the connectivity check entirely
      // and go straight to offline mode. This flag is consumed once and auto-resets.
      const electronAPI = (window as any).electronAPI;
      if (electronAPI?.popForceOffline) {
        try {
          const forced = await electronAPI.popForceOffline();
          if (forced) {
            logger.warn('keycloak.init', 'Force-offline flag set by main process — showing offline banner');
            setOnlineState(false);
            setInitialized(true);
            // Do NOT auto-retry on the native 'online' event here — that caused
            // a deadlock: brief connectivity flicker → auto-retry → Keycloak
            // navigation attempted → network drops again → failed load → blank.
            // The user must click "Tentar novamente" to explicitly retry auth.
            return;
          }
        } catch { /* ignore */ }
      }

      // Reliable reachability helper.
      // Uses renderer-side fetch (detects real offline + DevTools offline correctly)
      // AND the main-process electronNet check (consistent with navigation stack).
      // Both must succeed — this handles all offline scenarios consistently.
      const checkReachable = async (): Promise<boolean> => {
        // navigator.onLine is the fastest/most reliable signal. When Chromium detects
        // ERR_INTERNET_DISCONNECTED it sets this to false and blocks ALL HTTP (including
        // localhost). Short-circuit immediately to avoid trying fetch/IPC calls that
        // will also fail and potentially trigger navigation side-effects.
        if (!navigator.onLine) return false;

        const fetchCheck = (): Promise<boolean> => {
          const ctrl = new AbortController();
          const tid = setTimeout(() => ctrl.abort(), 4000);
          return fetch(`${keycloakUrl}/realms/${realm}`, {
            signal: ctrl.signal,
            cache: 'no-store',
            method: 'HEAD',
            mode: 'no-cors', // opaque response, but throws on actual network failure
          }).then(() => { clearTimeout(tid); return true; })
            .catch(() => { clearTimeout(tid); return false; });
        };

        const electronAPI = (window as any).electronAPI;
        if (electronAPI?.checkKeycloakReachable) {
          const [ipc, rend] = await Promise.all([
            electronAPI.checkKeycloakReachable().catch(() => false) as Promise<boolean>,
            fetchCheck(),
          ]);
          return ipc && rend;
        }
        return fetchCheck();
      };

      const keycloakInstance = new Keycloak({ url: keycloakUrl, realm, clientId });

      // Token persistence keys — lets Keycloak authenticate silently on subsequent startups
      // without any browser redirect or iframe SSO check.
      const KC_TOKEN_KEY = 'panther:kc:token';
      const KC_REFRESH_KEY = 'panther:kc:refresh';
      const KC_ID_KEY = 'panther:kc:idToken';
      const saveKcTokens = (kc: Keycloak) => {
        if (kc.token) localStorage.setItem(KC_TOKEN_KEY, kc.token);
        if (kc.refreshToken) localStorage.setItem(KC_REFRESH_KEY, kc.refreshToken);
        if (kc.idToken) localStorage.setItem(KC_ID_KEY, kc.idToken);
      };
      const clearKcTokens = () => {
        localStorage.removeItem(KC_TOKEN_KEY);
        localStorage.removeItem(KC_REFRESH_KEY);
        localStorage.removeItem(KC_ID_KEY);
      };

      const storedToken = localStorage.getItem(KC_TOKEN_KEY) ?? undefined;
      const storedRefresh = localStorage.getItem(KC_REFRESH_KEY) ?? undefined;
      const storedIdToken = localStorage.getItem(KC_ID_KEY) ?? undefined;
      const hasStoredTokens = !!storedToken && !!storedRefresh;

      // Always check reachability at startup — even when stored tokens exist.
      // This gives Discord-like behavior: offline screen immediately, regardless of
      // cached auth state. Uses main-process net.request() to bypass renderer cache.
      const reachable = await checkReachable();
      if (!reachable) {
        logger.warn('keycloak.init', 'Keycloak unreachable at startup, showing offline banner');
        setOnlineState(false);
        setInitialized(true);
        return;
      }

      // Confirmed online — clear any stale offline flag from localStorage.
      // If we started with isOnline=false (offline flag was set), this transitions
      // the UI from the offline overlay to the loading screen while Keycloak initializes.
      setOnlineState(true);

      logger.info('keycloak.init', hasStoredTokens ? 'Found stored tokens — will authenticate silently' : 'Keycloak reachable, initializing', { keycloakUrl, realm, clientId, hasStoredTokens });

      keycloakInstance
        .init({
          // When stored tokens exist, Keycloak JS uses them directly (silent refresh if
          // expired) without any browser redirect or iframe SSO check.
          // When no stored tokens: check-sso is used with silentCheckSsoFallback:false so
          // Keycloak NEVER redirects the main window on its own — our code handles it.
          token: storedToken,
          refreshToken: storedRefresh,
          idToken: storedIdToken,
          onLoad: hasStoredTokens ? undefined : 'check-sso',
          silentCheckSsoRedirectUri: hasStoredTokens ? undefined : (window.location.origin + '/silent-check-sso.html'),
          // Prevent Keycloak JS from doing its own full-page redirect when the iframe
          // silent check fails. Our code handles login() explicitly after checkReachable().
          silentCheckSsoFallback: false,
          checkLoginIframe: false,
          pkceMethod: 'S256',
          // offline_access: issues a long-lived offline refresh token (30-day idle by
          // default in Keycloak) instead of a regular session token (30-min idle).
          // This prevents logout after system sleep or prolonged inactivity.
          scope: 'openid profile email offline_access',
          redirectUri: window.location.origin + '/',
        })
        .then(async (authenticated) => {
          const endTime = Date.now();
          const duration = endTime - startTime;
          console.log(' [AUTH COMPLETE] Keycloak initialized in:', duration + 'ms');
          logger.info('keycloak.init', 'Keycloak initialized', { authenticated, duration });

          if (!authenticated) {
            // Stored tokens were invalid/expired — clear them so next startup re-checks.
            if (hasStoredTokens) clearKcTokens();

            // check-sso resolves authenticated=false both when offline AND when the user
            // genuinely has no session. isOnlineRef is unreliable (navigator.onLine returns
            // true in Electron dev even without internet). Do a fresh main-process check
            // via net.request() to distinguish the two cases reliably.
            const reachable = await checkReachable();
            if (!reachable) {
              logger.warn('keycloak.init', 'Keycloak unreachable after check-sso — showing offline banner');
              setOnlineState(false);
              setInitialized(true);
              return;
            }
            logger.info('keycloak.init', 'No active session, redirecting to login');
            const postLogout = localStorage.getItem('panther:kc:post_logout') === 'true';
            if (postLogout) localStorage.removeItem('panther:kc:post_logout');

            if (postLogout) {
              // After an explicit logout: show the "Você foi desconectado" screen
              // instead of auto-redirecting to Keycloak. This prevents immediate
              // auto-login while still letting the user choose when to log back in.
              // The user must take 3 deliberate steps: see screen → click "Fazer login"
              // → choose IDP — so IDP SSO at that point is intentional, not accidental.
              logger.info('keycloak.init', 'Post-logout: showing logged-out screen');
              keycloakInstanceRef.current = keycloakInstance;
              setIsLoggedOut(true);
              setInitialized(true);
              return;
            }

            keycloakInstance.login({
              redirectUri: window.location.origin + '/',
            });
            return;
          }

          setKeycloak(keycloakInstance);
          setAuthenticated(true);
          setInitialized(true);
          // Persist tokens so the next startup authenticates silently without a redirect.
          saveKcTokens(keycloakInstance);
          // Clear any stale offline flag from a previous session — the user is
          // now confirmed online and authenticated.
          setOnlineState(true);

          if (keycloakInstance.tokenParsed) {
          // Set the token for API calls
          setApiToken(keycloakInstance.token);
          // Extract data from token first (synchronous)
          const tokenParsed = keycloakInstance.tokenParsed as any;
          logger.debug('keycloak.token', 'Token parsed', {
            tokenKeys: Object.keys(tokenParsed),
            sub: tokenParsed.sub
          });

          // Get user info from token
          const username = tokenParsed.preferred_username || tokenParsed.username;
          const email = tokenParsed.email;
          const firstName = tokenParsed.given_name;
          const lastName = tokenParsed.family_name;
          const fullName = tokenParsed.name || `${firstName || ''} ${lastName || ''}`.trim() || username;

          logger.debug('keycloak.user', 'User info extracted from token', { username, email, firstName, lastName, fullName });

          // Set user immediately from token
          setUser({
            id: tokenParsed.sub,
            username: username,
            email: email,
            firstName: firstName,
            lastName: lastName,
            name: fullName,
          });

          // Get client-specific roles (pantheraccess-web)
          const resourceAccess = tokenParsed.resource_access?.['pantheraccess-web'] || tokenParsed.resource_access?.['pantheraccess'];
          const clientRoles = resourceAccess?.roles || [];

          // Get realm roles
          const realmRoles = tokenParsed.realm_access?.roles || [];

          // Combine roles (prefer client roles, then realm roles)
          const allRoles = Array.from(new Set([...clientRoles, ...realmRoles]));

          logger.debug('keycloak.roles', 'Roles extracted from token', {
            clientRoles,
            realmRoles,
            allRoles,
            rolesCount: allRoles.length
          });

          if (allRoles.length === 0) {
            logger.warn('keycloak.roles', 'No roles found in token', {
              tokenKeys: Object.keys(tokenParsed)
            });
          }

          setRoles(allRoles);

          // Check for super admin
          const isSA = allRoles.includes('super_admin') || allRoles.includes('panther_super_admin') || allRoles.includes('condo_super_admin');
          setIsSuperAdmin(isSA);
          logger.info('keycloak.roles', 'Super admin status', { isSuperAdmin: isSA });

          // Extract groups (condos) from token
          const userGroups = tokenParsed.groups || [];
          setGroups(userGroups);

          // Extract condo IDs from groups (format: /condo_{uuid})
          const condoIds = userGroups
            .filter((group: string) => group.startsWith('/condo_'))
            .map((group: string) => group.replace('/condo_', ''));
          setCondos(condoIds);

          // Extract customer-scoped roles from groups (format: /customer_{uuid}/{role})
          const customerRolesMap = new Map<string, string[]>();

          // DEBUG: Log ALL groups from token
          console.log('=== KEYCLOAK TOKEN GROUPS DEBUG ===');
          console.log('User ID (sub):', tokenParsed.sub);
          console.log('Username:', tokenParsed.preferred_username);
          console.log('Email:', tokenParsed.email);
          console.log('ALL Groups in Token:', userGroups);
          console.log('Customer Groups:', userGroups.filter((g: string) => g.startsWith('/customer_')));

          userGroups
            .filter((group: string) => group.startsWith('/customer_'))
            .forEach((group: string) => {
              // Parse group path: /customer_{uuid}/{role}
              const match = group.match(/^\/customer_([a-f0-9-]+)\/(.+)$/);
              if (match) {
                const customerId = match[1];
                const role = match[2];

                console.log(`  → Parsed customer group: customerId=${customerId}, role=${role}`);

                if (!customerRolesMap.has(customerId)) {
                  customerRolesMap.set(customerId, []);
                }
                customerRolesMap.get(customerId)!.push(role);
              }
            });

          // Convert map to array of CustomerRole objects
          const extractedCustomerRoles: CustomerRole[] = Array.from(customerRolesMap.entries()).map(
            ([customerId, roles]) => ({ customerId, roles })
          );
          setCustomerRoles(extractedCustomerRoles);

          console.log('=== EXTRACTED CUSTOMER ROLES ===', extractedCustomerRoles);

          logger.debug('keycloak.groups', 'Groups, condos, and customer roles extracted', {
            userGroups,
            condoIds,
            customerRoles: extractedCustomerRoles
          });

          // Also load user profile asynchronously (for additional data if needed)
          keycloakInstance.loadUserProfile().then((profile) => {
            logger.debug('keycloak.profile', 'User profile loaded', {
              id: profile.id,
              username: profile.username,
              email: profile.email
            });
            // Update user with profile data if it has more info
            setUser(prevUser => ({
              ...prevUser,
              id: profile.id || prevUser?.id,
              username: profile.username || prevUser?.username,
              email: profile.email || prevUser?.email,
              firstName: profile.firstName || prevUser?.firstName,
              lastName: profile.lastName || prevUser?.lastName,
              name: profile.firstName || profile.lastName
                ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
                : prevUser?.name,
            }));
          }).catch((error) => {
            logger.warn('keycloak.profile', 'Failed to load user profile', { error: error.message });
          });
        }

        const handleRefreshError = async (err: any) => {
          logger.error('keycloak.token', 'Token refresh failed, probing connectivity', { error: err });
          const stillReachable = await checkReachable();
          if (!stillReachable) {
            logger.warn('keycloak.token', 'Keycloak unreachable — showing offline banner');
            clearKcTokens();
            setOnlineState(false);
          } else {
            // Server reachable but session expired (e.g. after system sleep).
            // Dispatch event so the UI can show a "session expired" prompt.
            logger.warn('keycloak.token', 'Session expired — dispatching session-expired event');
            clearKcTokens();
            window.dispatchEvent(new CustomEvent('panther:session-expired'));
          }
        };

        // The single funnel described at inflightRefreshRef. Every trigger goes through
        // here; concurrent callers share one attempt, and the failure path runs once.
        //
        // A joining caller inherits the in-flight call's minValidity rather than its own,
        // which is safe in both directions. updateToken(240) already refreshes whenever
        // fewer than 240 seconds remain — expired tokens very much included — so a forced
        // (-1) caller that joins an interval tick still ends up holding a live token. The
        // only case where a forced refresh is "lost" is when the token has ample life
        // left, and there was nothing to fix then.
        const refreshTokens: RefreshTokensFn = (minValidity, reason) => {
          const existing = inflightRefreshRef.current;
          if (existing) {
            logger.debug('keycloak.token', 'Joining in-flight refresh', { reason });
            return existing;
          }

          logger.debug('keycloak.token', 'Starting refresh', { reason, minValidity });

          const attempt: Promise<RefreshOutcome> = keycloakInstance
            .updateToken(minValidity)
            .then((refreshed): RefreshOutcome => {
              if (refreshed && keycloakInstance.token) {
                saveKcTokens(keycloakInstance);
                setApiToken(keycloakInstance.token);
                logger.info('keycloak.token', 'Token refreshed', {
                  reason,
                  tokenLength: keycloakInstance.token.length,
                });
              }
              return { ok: true, refreshed };
            })
            .catch(async (err): Promise<RefreshOutcome> => {
              await handleRefreshError(err);
              return { ok: false };
            })
            .finally(() => {
              inflightRefreshRef.current = null;
            });

          inflightRefreshRef.current = attempt;
          return attempt;
        };

        // Exposed so the context's refreshToken and retryAuth, which are defined outside
        // this IIFE, share the same mutex rather than opening a competing refresh.
        refreshTokensRef.current = refreshTokens;

        // Built-in Keycloak hook: fires the moment the access token expires.
        // Immediately issue a new one via the refresh token — this is the primary
        // guard against "token expired" errors during normal app usage. It adds
        // exactly one Keycloak request per token lifetime (typically every 5 min),
        // with no polling overhead.
        keycloakInstance.onTokenExpired = () => {
          logger.info('keycloak.token', 'onTokenExpired fired — refreshing immediately');
          // Persisting, publishing and error handling all live in refreshTokens now.
          void refreshTokens(5, 'onTokenExpired');
        };

        // Token refresh: check every 10 seconds, refresh if token expires in < 4 min.
        // Never calls login() directly — instead shows the network banner and lets
        // the user (or auto-recovery) trigger re-login explicitly.
        // Combined token-refresh + connectivity probe — runs every 10 s.
        // Browser 'offline' event is unreliable in Electron (WiFi connected but no WAN),
        // so we actively probe via main-process net.request() on every tick.
        // This gives Discord-like detection: banner appears within ~10 s of going offline.
        refreshInterval = setInterval(async () => {
          if (!isOnlineRef.current) return; // already offline, nothing to do

          const outcome = await refreshTokens(240, 'interval');

          if (!outcome.ok) {
            // refreshTokens already probed connectivity and set the offline banner or
            // announced session expiry; nothing further to do here.
            return;
          }

          if (!outcome.refreshed) {
            // Token still valid — probe connectivity to catch mid-session offline.
            const stillReachable = await checkReachable();
            if (!stillReachable) {
              logger.warn('keycloak.probe', 'Connectivity probe failed — showing offline banner');
              setOnlineState(false);
            } else if (!isOnlineRef.current) {
              // Reachable again after a blip the 'online' event did not deliver.
              setOnlineState(true);
            }
          }
        }, 10000);

        // Wake-from-sleep / window-focus recovery: when the page becomes visible again
        // after being hidden (minimized, system sleep, screen lock), immediately try
        // to refresh instead of waiting up to 10s for the next interval tick.
        // This is the primary guard against "session expired after sleep" scenarios.
        const handleVisibilityChange = async () => {
          if (document.visibilityState !== 'visible') return;
          if (!isOnlineRef.current) return;
          logger.info('keycloak.visibility', 'Page became visible — force-refreshing token');
          // Force refresh regardless of remaining lifetime: the app may have been
          // sleeping and the token could be expired or about to expire. Going through
          // refreshTokens means that if an interval tick is already refreshing, this
          // joins it instead of racing it for the same refresh token.
          await refreshTokens(-1, 'visibilitychange');
        };
        visibilityListener = handleVisibilityChange;
        document.addEventListener('visibilitychange', handleVisibilityChange);
      })
        .catch(async (error) => {
          logger.error('keycloak.init', 'Keycloak initialization failed', error);
          // Could be: (a) network dropped between fetch check and here → show offline
          //           (b) stored refresh token expired (400) → clear tokens and retry login
          const stillReachable = await checkReachable();
          if (!stillReachable) {
            setOnlineState(false);
            setInitialized(true);
          } else {
            // Server is reachable — init failed due to bad/expired tokens.
            // Clear them and redirect to login so the user can re-authenticate.
            logger.warn('keycloak.init', 'Init failed but server reachable — clearing tokens and redirecting to login');
            clearKcTokens();
            keycloakInstance.login({ redirectUri: window.location.origin + '/' });
          }
        });

      // Electron main process blocks Keycloak navigation when offline
      removeAuthOfflineListener = electronAPI?.onAuthOffline?.(() => setOnlineState(false));

      // native online/offline events — recovery path when real network restores
      handleOnline = () => {
        void refreshTokensRef.current?.(-1, 'online-event').then((outcome) => {
          // Only clear the banner once we know the refresh actually went through.
          // Declaring "online" on a failed refresh hid a genuinely broken session.
          if (outcome.ok) setOnlineState(true);
        });
      };
      window.addEventListener('online', handleOnline);
    })();

    // Cleanup on unmount — accesses outer-scope vars set by the async IIFE above
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
      if (handleOnline) window.removeEventListener('online', handleOnline);
      if (handleOffline) window.removeEventListener('offline', handleOffline);
      removeAuthOfflineListener?.();
      // Must pass the same function instance that was registered. Passing a fresh
      // arrow function here removed nothing, so every remount of this provider left
      // another live listener behind — each closed over its own (by then dead)
      // Keycloak instance, and each still firing updateToken on visibility change.
      if (visibilityListener) {
        document.removeEventListener('visibilitychange', visibilityListener);
        visibilityListener = null;
      }
    };
  }, []);

  const refreshToken = async (): Promise<boolean> => {
    if (!keycloak) {
      logger.warn('keycloak.refresh', 'Cannot refresh token - keycloak not initialized');
      return false;
    }

    // This is the callback api.ts invokes on a 401, so it runs exactly when the
    // background interval is most likely to also be refreshing. Sharing the mutex is
    // what stops those two from racing for the same refresh token.
    const viaFunnel = refreshTokensRef.current;
    if (viaFunnel) {
      logger.info('keycloak.refresh', 'Attempting to refresh token (forced)');
      const outcome = await viaFunnel(-1, 'api-401');
      if (!outcome.ok) return false;
      // A still-valid token is a usable token: report success so the caller retries.
      if (keycloak.token) {
        setApiToken(keycloak.token);
        return true;
      }
      logger.warn('keycloak.refresh', 'Token refresh did not produce new token');
      return false;
    }

    // Init has not finished wiring the funnel yet; fall back to a direct refresh.
    try {
      logger.info('keycloak.refresh', 'Attempting to refresh token (forced, pre-init)');
      const refreshed = await keycloak.updateToken(-1);

      if (keycloak.token) {
        logger.info('keycloak.refresh', 'Token refreshed successfully', {
          refreshed,
          tokenAvailable: !!keycloak.token,
          tokenLength: keycloak.token?.length
        });
        setApiToken(keycloak.token);
        return true;
      }

      logger.warn('keycloak.refresh', 'Token refresh did not produce new token');
      return false;
    } catch (error) {
      logger.error('keycloak.refresh', 'Failed to refresh token', error);
      // Never auto-navigate to Keycloak here. The background refresh interval
      // handles online/offline detection. Calling login() from this callback
      // navigates the Electron window to the auth server (blue screen).
      return false;
    }
  };

  // Register token refresh callback when keycloak is initialized
  useEffect(() => {
    if (keycloak && initialized) {
      logger.debug('keycloak.callback', 'Registering token refresh callback');
      setTokenRefreshCallback(refreshToken);
    }
  }, [keycloak, initialized]);

  // Ensure animation always completes (just logo + letters, no extra waiting)
  useEffect(() => {
    const ANIMATION_DURATION = 1320; // Logo (600ms) + Last letter starts (600+120*5=1200ms) + letter animation (400ms) = 1600ms, but we use 1320ms to transition right as last letter appears
    console.log(' [ANIMATION START] Logo and letters animation starting...');
    const timer = setTimeout(() => {
      console.log(' [ANIMATION COMPLETE] Animation finished at:', ANIMATION_DURATION + 'ms');
      setAnimationComplete(true);
    }, ANIMATION_DURATION);

    return () => clearTimeout(timer);
  }, []);

  const login = () => {
    if (keycloak) {
      // Always use window.location.origin to support both web and Electron
      const redirectUrl = window.location.origin;
      logger.info('keycloak.login', 'Redirecting to login', { redirectUrl: redirectUrl + '/' });
      keycloak.login({
        redirectUri: redirectUrl + '/',
        scope: 'openid profile email offline_access',
      });
    }
  };

  // Called by NetworkStatusBanner — tries silent refresh first, falls back to login()
  const retryAuth = () => {
    if (!keycloak) {
      // Keycloak never initialized (offline from the very start).
      // Re-mount KeycloakProvider via retryKey instead of a full page reload —
      // window.location.reload() would fail in dev mode when offline (ERR_INTERNET_DISCONNECTED).
      window.dispatchEvent(new CustomEvent('panther:retry-auth'));
      return;
    }
    keycloak.updateToken(-1)
      .then((refreshed) => {
        setOnlineState(true);
        if (refreshed && keycloak.token) {
          setApiToken(keycloak.token);
        }
      })
      .catch(async () => {
        // Session expired — verify we can reach Keycloak before navigating away,
        // otherwise we'd get a blue screen on a machine that's still offline.
        if (!navigator.onLine) { setOnlineState(false); return; }
        const electronAPI = (window as any).electronAPI;
        let reachable: boolean;
        if (electronAPI?.checkKeycloakReachable) {
          reachable = await electronAPI.checkKeycloakReachable();
        } else {
          try {
            const ctrl = new AbortController();
            const tid = setTimeout(() => ctrl.abort(), 4000);
            await fetch(
              `${process.env.REACT_APP_KEYCLOAK_URL || window.location.origin}/realms/${process.env.REACT_APP_KEYCLOAK_REALM || 'panthercondos'}`,
              { signal: ctrl.signal, cache: 'no-store', method: 'HEAD', mode: 'no-cors' }
            );
            clearTimeout(tid);
            reachable = true;
          } catch {
            reachable = false;
          }
        }
        if (reachable) {
          login();
        } else {
          setOnlineState(false);
        }
      });
  };

  const logout = () => {
    if (!keycloak) return;

    // Clear stored tokens immediately so next startup forces a fresh login.
    localStorage.removeItem('panther:kc:token');
    localStorage.removeItem('panther:kc:refresh');
    localStorage.removeItem('panther:kc:idToken');
    localStorage.setItem('panther:kc:post_logout', 'true');

    // Terminate the server-side panthercondos session and redirect back to the app.
    // Keycloak needs its auth-flow cookies (AUTH_SESSION_ID etc.) intact to process
    // the logout and issue the post_logout_redirect, so we do NOT clear cookies here.
    // Cookie clearing happens in the onLogin handler below, right before the next
    // keycloak.login() call — at that point Keycloak will create fresh cookies anyway.
    logger.info('keycloak.logout', 'Logging out of panthercondos');
    keycloak.logout({ redirectUri: window.location.origin + '/' });
  };

  const hasRole = (role: string): boolean => {
    return roles.includes(role);
  };

  const hasAnyRole = (...requiredRoles: string[]): boolean => {
    return requiredRoles.some(role => roles.includes(role));
  };

  const hasAccessToCondo = (condoId: string): boolean => {
    // Super admins have access to all condos
    if (isSuperAdmin) {
      return true;
    }
    // Check if user is in the condo group
    return condos.includes(condoId);
  };

  const getRolesForCustomer = (customerId: string): string[] => {
    // Super admins have all roles for all customers
    if (isSuperAdmin) {
      console.log('[getRolesForCustomer] User is super admin');
      return ['super_admin'];
    }

    // Find the customer in customerRoles array
    const customerRole = customerRoles.find(cr => cr.customerId === customerId);
    console.log('[getRolesForCustomer] DEBUG:', {
      customerId,
      allCustomerRoles: customerRoles,
      foundRole: customerRole,
      returning: customerRole ? customerRole.roles : []
    });
    return customerRole ? customerRole.roles : [];
  };

  const value: KeycloakContextType = {
    keycloak,
    initialized,
    authenticated,
    user,
    roles,
    condos,
    groups,
    customerRoles,
    isSuperAdmin,
    hasRole,
    hasAnyRole,
    hasAccessToCondo,
    getRolesForCustomer,
    login,
    logout,
    refreshToken,
    retryAuth,
    token: keycloak?.token,
    isOnline,
  };

  // When offline, render children immediately so NetworkStatusBanner can show —
  // Keycloak's init() never resolves while blocked, so we must bypass the loading check.
  if (!isOnline) {
    return (
      <KeycloakContext.Provider value={value}>
        {children}
      </KeycloakContext.Provider>
    );
  }

  // After an explicit logout, show the logged-out screen instead of auto-redirecting to Keycloak.
  if (isLoggedOut) {
    return (
      <LoggedOutScreen
        onLogin={async () => {
          // Clear Keycloak SSO cookies so the pantherinternal IDP session is gone
          // before starting a fresh login flow — prevents auto-login on re-entry.
          const electronAPI = (window as any).electronAPI;
          if (electronAPI?.clearKeycloakCookies) {
            await electronAPI.clearKeycloakCookies();
          }
          setIsLoggedOut(false);
          keycloakInstanceRef.current?.login({
            redirectUri: window.location.origin + '/',
            prompt: 'login',
          });
        }}
      />
    );
  }

  // Show loading screen while animation plays OR while Keycloak initializes
  // Animation always plays first, then spinner shows only if still loading
  if (!initialized || !animationComplete) {
    return <LoadingScreen variant="full" message="Autenticando..." showLogo={true} />;
  }

  return (
    <KeycloakContext.Provider value={value}>
      {children}
    </KeycloakContext.Provider>
  );
};
